import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function LoginPage() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [showForgot, setShowForgot] = useState(false);
    const { login } = useAuth();
    const nav = useNavigate();

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setBusy(true);
        try {
            await login(email, password);
            nav("/", { replace: true });
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
        setBusy(false);
    };

    return (
        <div
            data-testid="login-page"
            className="min-h-screen w-full flex items-center justify-center px-4 py-10"
            style={{ background: "var(--tms-navy)" }}
        >
            <div className="w-full max-w-5xl grid gap-10 md:grid-cols-2 items-center">
                {/* LEFT: brand splash */}
                <div className="text-white rise-in">
                    <div className="flex items-center gap-3 mb-8">
                        <div className="brand-mark brand-mark-lg" style={{ background: "var(--tms-green)", color: "var(--tms-navy)" }}>
                            ES
                        </div>
                        <div className="flex flex-col">
                            <span style={{ fontSize: 20, fontWeight: 600 }}>Email Snippet Manager</span>
                            <span
                                style={{
                                    color: "var(--tms-green)",
                                    fontSize: 11,
                                    letterSpacing: "0.14em",
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                }}
                            >
                                Email template library
                            </span>
                        </div>
                    </div>
                    <h1 style={{ fontSize: 34, lineHeight: 1.15, fontWeight: 600, marginBottom: 14 }}>
                        The shared inbox for<br />advice-ready email.
                    </h1>
                    <p style={{ color: "rgba(255,255,255,0.72)", maxWidth: 420, fontSize: 14, lineHeight: 1.55 }}>
                        One tidy library of client emails for the whole team at The Mortgage Stop.
                        Fill in the details, copy the formatted email, paste into Outlook.
                    </p>
                </div>

                {/* RIGHT: sign-in card */}
                <div className="flex items-center justify-center rise-in" style={{ animationDelay: "80ms" }}>
                    <div className="tms-card w-full max-w-md p-8">
                        <div className="flex items-center gap-2 mb-6">
                            <Mail size={18} style={{ color: "var(--tms-navy)" }} />
                            <span style={{ fontSize: 18, fontWeight: 600, color: "var(--tms-navy)" }}>Sign in</span>
                        </div>

                        <form onSubmit={onSubmit} className="space-y-4">
                            <div>
                                <label className="block text-xs font-semibold mb-1" style={{ color: "var(--tms-muted)" }}>
                                    Work email
                                </label>
                                <input
                                    data-testid="login-email-input"
                                    type="email"
                                    className="tms-input"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@themortgagestop.co.uk"
                                    autoComplete="email"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold mb-1" style={{ color: "var(--tms-muted)" }}>
                                    Password
                                </label>
                                <input
                                    data-testid="login-password-input"
                                    type="password"
                                    className="tms-input"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Your password"
                                    autoComplete="current-password"
                                    required
                                />
                            </div>
                            {error && (
                                <div
                                    data-testid="login-error"
                                    style={{
                                        background: "#fdeceb",
                                        color: "var(--tms-danger)",
                                        padding: "8px 12px",
                                        borderRadius: 8,
                                        fontSize: 13,
                                    }}
                                >
                                    {error}
                                </div>
                            )}
                            <button
                                type="submit"
                                data-testid="login-submit-btn"
                                className="btn-primary-green w-full justify-center"
                                disabled={busy}
                                style={{ padding: "12px 16px" }}
                            >
                                {busy && <Loader2 size={14} className="animate-spin" />}
                                {busy ? "Signing in…" : "Sign in"}
                            </button>
                        </form>
                        <div className="mt-4 text-center">
                            <button
                                type="button"
                                onClick={() => setShowForgot((v) => !v)}
                                data-testid="login-forgot-password-link"
                                style={{
                                    background: "none",
                                    border: "none",
                                    padding: 0,
                                    color: "var(--tms-navy)",
                                    fontSize: 12,
                                    fontWeight: 500,
                                    cursor: "pointer",
                                    textDecoration: "underline",
                                    textUnderlineOffset: 3,
                                }}
                            >
                                Forgotten your password?
                            </button>
                            {showForgot && (
                                <div
                                    data-testid="login-forgot-password-note"
                                    style={{
                                        marginTop: 10,
                                        background: "#F3F5F7",
                                        color: "var(--tms-navy)",
                                        padding: "10px 12px",
                                        borderRadius: 10,
                                        fontSize: 12,
                                        lineHeight: 1.55,
                                        textAlign: "left",
                                        border: "1px solid var(--tms-border)",
                                    }}
                                >
                                    Contact your administrator to reset your password — they can issue a temporary one from Settings → Team, and you&apos;ll set your own on next sign-in.
                                </div>
                            )}
                        </div>
                        <p className="mt-5 text-center" style={{ color: "var(--tms-muted)", fontSize: 12 }}>
                            Need access? Ask your team administrator for an invite.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}
